import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Users, UserPlus, IndianRupee, Search, Download, X } from "lucide-react";
import api, { API } from "@/lib/api";

export default function CreatorContactsSection() {
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState("");
  const [openContact, setOpenContact] = useState(null);

  const load = () => {
    api.get("/creator/crm/contacts", { params: { search: search || undefined } }).then((r) => setContacts(r.data.contacts));
  };

  useEffect(() => {
    api.get("/creator/crm/stats").then((r) => setStats(r.data));
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const viewContact = async (id) => {
    const { data } = await api.get(`/creator/crm/contacts/${id}`).catch(() => { toast.error("Could not load contact"); return {}; });
    if (data) setOpenContact(data);
  };

  return (
    <section data-testid="creator-contacts-section">
      <h2 className="text-lg font-heading font-bold text-white mb-4">My Contacts</h2>
      {stats && (
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div className="glass-panel rounded-xl p-4" data-testid="creator-contacts-stat-buyers">
            <Users className="w-4 h-4 text-indigo-400 mb-2" />
            <p className="text-2xl font-bold text-white">{stats.total_buyers}</p>
            <p className="text-xs text-slate-400">Total Buyers</p>
          </div>
          <div className="glass-panel rounded-xl p-4" data-testid="creator-contacts-stat-new">
            <UserPlus className="w-4 h-4 text-emerald-400 mb-2" />
            <p className="text-2xl font-bold text-white">{stats.new_this_week}</p>
            <p className="text-xs text-slate-400">New This Week</p>
          </div>
          <div className="glass-panel rounded-xl p-4" data-testid="creator-contacts-stat-revenue">
            <IndianRupee className="w-4 h-4 text-amber-400 mb-2" />
            <p className="text-2xl font-bold text-white">₹{stats.revenue}</p>
            <p className="text-xs text-slate-400">Revenue</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between gap-3 mb-3">
        <div className="relative flex-1 max-w-xs">
          <Search className="w-3.5 h-3.5 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()}
            placeholder="Search your buyers" data-testid="creator-contacts-search-input"
            className="w-full bg-white/5 border border-white/10 rounded-lg pl-8 pr-3 py-2 text-xs text-white" />
        </div>
        <a href={`${API}/creator/crm/contacts/export`} data-testid="creator-contacts-export-btn"
          className="flex items-center gap-1.5 text-xs font-semibold bg-white/5 hover:bg-white/10 text-white px-3 py-2 rounded-lg"><Download className="w-3.5 h-3.5" /> Export</a>
      </div>

      <div className="glass-panel rounded-2xl overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-slate-400 border-b border-white/10">
              <th className="p-3">Name</th><th className="p-3">Email</th><th className="p-3">City</th><th className="p-3">Joined</th>
            </tr>
          </thead>
          <tbody>
            {contacts.map((c) => (
              <tr key={c.id} className="border-b border-white/5 last:border-0 cursor-pointer hover:bg-white/5" onClick={() => viewContact(c.id)} data-testid={`creator-contact-row-${c.id}`}>
                <td className="p-3 text-white">{c.name}</td>
                <td className="p-3 text-slate-400">{c.email}</td>
                <td className="p-3 text-slate-400">{c.city || "—"}</td>
                <td className="p-3 text-slate-400">{new Date(c.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
            {contacts.length === 0 && (
              <tr><td colSpan={4} className="p-6 text-center text-slate-400 text-sm" data-testid="creator-no-contacts">No buyers yet — they'll show up here automatically after a course purchase.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {openContact && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpenContact(null)} data-testid="creator-contact-detail-modal">
          <div className="glass-panel rounded-2xl p-5 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-semibold text-white">{openContact.contact.name}</p>
              <button onClick={() => setOpenContact(null)}><X className="w-4 h-4 text-slate-400 hover:text-white" /></button>
            </div>
            <p className="text-xs text-slate-400 mb-3">{openContact.contact.email} · {openContact.contact.phone || "no phone"}</p>
            <p className="text-xs font-semibold text-slate-300 mb-2">Purchases from you</p>
            {openContact.purchases.length === 0 && <p className="text-xs text-slate-500">No recorded purchases.</p>}
            {openContact.purchases.map((p) => (
              <div key={p.id || p.session_id} className="text-xs text-slate-300 border-b border-white/5 py-1.5">{p.package_id} — ₹{p.amount}</div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
